import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  GlobeAltIcon,
  DocumentTextIcon,
  BanknotesIcon,
  ArrowTopRightOnSquareIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';
import { esgApi, EsgDeclaration, carbonApi } from '../../api/carbon';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { formatTND } from '../../utils/formatters';

const PALETTE = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#6366f1', '#14b8a6', '#ec4899'];

const SCOPE_LABELS: Record<string, string> = {
  SCOPE_1: 'Scope 1 — émissions directes',
  SCOPE_2: 'Scope 2 — énergie',
  SCOPE_3: 'Scope 3 — chaîne de valeur',
  '1': 'Scope 1 — émissions directes',
  '2': 'Scope 2 — énergie',
  '3': 'Scope 3 — chaîne de valeur',
};

interface Slice {
  name: string;
  value: number;
}

/** GTS owns the annual-report schema, so we read it defensively across a few likely key
 *  names rather than coupling to one exact shape. Returns kg CO2e. */
function pickTotal(data: Record<string, any> | undefined): number {
  if (!data) return 0;
  const direct = data.totalKgCo2e ?? data.total ?? data.totalCo2e ?? data.totalKg ?? data.kgCo2e;
  if (typeof direct === 'number') return direct;
  // Fall back to summing whatever breakdown we can find.
  const byScope = normaliseMap(data.byScope ?? data.scopes);
  if (byScope.length > 0) return byScope.reduce((s, x) => s + x.value, 0);
  return 0;
}

/** Accepts either a `{ key: number }` map or an array of `{ name/scope/category, value/kgCo2e }`. */
function normaliseMap(raw: any): Slice[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((e) => ({
        name: String(e.name ?? e.scope ?? e.category ?? e.label ?? ''),
        value: Number(e.value ?? e.kgCo2e ?? e.total ?? e.totalKgCo2e ?? 0),
      }))
      .filter((e) => e.name && Number.isFinite(e.value));
  }
  if (typeof raw === 'object') {
    return Object.entries(raw)
      .map(([name, value]) => ({ name, value: Number(value) }))
      .filter((e) => Number.isFinite(e.value));
  }
  return [];
}

export function DeclarationEsg() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [esg, setEsg] = useState<EsgDeclaration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    esgApi
      .annual(year)
      .then((d) => { if (!cancelled) setEsg(d); })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 402 || status === 403) {
          setError("La déclaration ESG est réservée au plan Business. Mettez à niveau votre abonnement pour y accéder.");
        } else {
          setError(e?.response?.data?.message || "Impossible de charger la déclaration ESG.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year]);

  const carbonAvailable = Boolean(esg?.carbon?.available);
  const total = useMemo(() => pickTotal(esg?.carbon?.data), [esg]);

  const byScope = useMemo(() => {
    const data = esg?.carbon?.data;
    const slices = normaliseMap(data?.byScope ?? data?.scopes);
    return slices.map((s) => ({ ...s, label: SCOPE_LABELS[s.name] || s.name }));
  }, [esg]);

  const byCategory = useMemo(() => {
    const data = esg?.carbon?.data;
    return normaliseMap(data?.byCategory ?? data?.categories)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [esg]);

  // Carbon intensity: kg CO2e per 1000 TND of revenue — the headline CSRD-style ratio.
  const intensity = useMemo(() => {
    const ca = esg?.financial?.ca ?? 0;
    return ca > 0 ? (total / (ca / 1000)) : 0;
  }, [total, esg]);

  const openGts = async () => {
    try {
      const { redirectUrl } = await carbonApi.startSso('/dashboard');
      window.location.href = redirectUrl;
    } catch {
      window.alert("Impossible d'ouvrir GTS Carbon — vérifiez votre abonnement.");
    }
  };

  const years = useMemo(
    () => Array.from({ length: 5 }, (_, i) => currentYear - i),
    [currentYear],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="page-title">Déclaration ESG</h1>
          <p className="page-subtitle">
            Synthèse annuelle environnementale, sociale et de gouvernance (CSRD-light)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="sr-only" htmlFor="esg-year">Année</label>
          <select
            id="esg-year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="rounded-lg border-gray-300 text-sm"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => window.print()}
            disabled={!esg}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <PrinterIcon className="h-4 w-4" />
            Imprimer
          </button>
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {!loading && error && (
        <div className="card border-amber-200 bg-amber-50" role="alert">
          <p className="text-sm text-amber-800">{error}</p>
        </div>
      )}

      {!loading && !error && esg && (
        <>
          <div className="hidden print:block">
            <h1 className="text-xl font-bold text-gray-900">Déclaration ESG — exercice {esg.year}</h1>
          </div>

          {/* Financier */}
          <section>
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Indicateurs financiers — exercice {esg.year}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <Kpi
                icon={<BanknotesIcon className="h-5 w-5 text-emerald-500" />}
                label="Chiffre d'affaires HT"
                value={formatTND(esg.financial.ca)}
                accent="text-emerald-600"
              />
              <Kpi
                icon={<DocumentTextIcon className="h-5 w-5 text-blue-500" />}
                label="Factures émises"
                value={esg.financial.nbFactures.toLocaleString('fr-TN')}
              />
              <Kpi
                icon={<GlobeAltIcon className="h-5 w-5 text-green-600" />}
                label="Intensité carbone"
                value={carbonAvailable ? `${intensity.toLocaleString('fr-TN', { maximumFractionDigits: 1 })} kg/kTND` : '—'}
                accent="text-green-700"
              />
            </div>
          </section>

          {/* Carbone */}
          <section>
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Empreinte carbone — GTS Carbon</h2>
            {!carbonAvailable ? (
              <div className="card border-green-200 bg-green-50">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 flex-shrink-0 rounded-lg bg-green-100 flex items-center justify-center">
                    <GlobeAltIcon className="h-6 w-6 text-green-700" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-700">
                      Les données d'émissions ne sont pas disponibles
                      {esg.carbon.reason ? ` (${esg.carbon.reason})` : ''}. Connectez-vous à GTS Carbon
                      pour calculer et synchroniser votre empreinte annuelle.
                    </p>
                    <button
                      type="button"
                      onClick={openGts}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700"
                    >
                      Ouvrir GTS Carbon
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="card">
                  <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">
                    Émissions totales {esg.year}
                  </div>
                  <div className="mt-1 text-3xl font-semibold text-green-700">
                    {total.toLocaleString('fr-TN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    <span className="text-base font-normal text-gray-500 ml-1">kg CO₂e</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="card">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Répartition par scope (GHG Protocol)</h3>
                    {byScope.length === 0 ? (
                      <p className="text-sm text-gray-500">Pas de ventilation par scope fournie.</p>
                    ) : (
                      <div style={{ width: '100%', height: 240 }}>
                        <ResponsiveContainer>
                          <PieChart>
                            <Pie
                              data={byScope}
                              dataKey="value"
                              nameKey="label"
                              outerRadius={85}
                              label={(e: any) => `${(e.percent * 100).toFixed(0)}%`}
                            >
                              {byScope.map((_, i) => (
                                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                              ))}
                            </Pie>
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Tooltip formatter={(v: any) => `${Number(v).toLocaleString('fr-TN')} kg CO₂e`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  <div className="card">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Principaux postes d'émission</h3>
                    {byCategory.length === 0 ? (
                      <p className="text-sm text-gray-500">Pas de ventilation par catégorie fournie.</p>
                    ) : (
                      <div style={{ width: '100%', height: 240 }}>
                        <ResponsiveContainer>
                          <BarChart data={byCategory} layout="vertical" margin={{ left: 12, right: 12 }}>
                            <CartesianGrid stroke="#f3f4f6" horizontal={false} />
                            <XAxis type="number" tick={{ fontSize: 11 }} stroke="#9ca3af"
                              tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                            <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} stroke="#9ca3af" />
                            <Tooltip formatter={(v: any) => `${Number(v).toLocaleString('fr-TN')} kg CO₂e`} />
                            <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </section>

          <p className="text-xs text-gray-400 border-t border-gray-100 pt-3">{esg.disclaimer}</p>
        </>
      )}
    </div>
  );
}

interface KpiProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent?: string;
}
const Kpi: React.FC<KpiProps> = ({ icon, label, value, accent = 'text-gray-700' }) => (
  <div className="card">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-wide text-gray-500 font-medium">{label}</div>
        <div className={`mt-1 text-lg font-semibold ${accent}`}>{value}</div>
      </div>
      {icon}
    </div>
  </div>
);

export default DeclarationEsg;
